import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { LogOut, Plus, Pencil, Trash2, Users, Calendar, BarChart3, X, CalendarDays, FileDown, GripVertical, History, Star, MessageSquare, QrCode, Settings, ChevronLeft, ChevronRight, Fingerprint, MapPin, UserCheck, RefreshCw, ShieldCheck, Copy, TableProperties } from 'lucide-react';
import WeeklyCalendar from '@/components/WeeklyCalendar';
import DailyCalendar from '@/components/DailyCalendar';
import FamilyHistory from '@/components/FamilyHistory';
import PoolMap from '@/components/PoolMap';
import ScheduleTable from '@/components/ScheduleTable';
import { StarDisplay } from '@/components/ReviewForm';
import QRCode from 'react-qr-code';

// "Hoy" en la zona horaria de Canarias (en-CA → formato YYYY-MM-DD), no UTC.
const canaryToday = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Atlantic/Canary' });

export default function AdminDashboard() {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('workshops');
  const [workshops, setWorkshops] = useState([]);
  const [families, setFamilies] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', date: '', time_start: '', time_end: '', max_capacity: 15, age_min: 3, age_max: 14, category: 'general', time_slot: 'morning', registration_type: 'mandatory', materials: '', pool_zone: '', pool_location: null, drive_url: '' });
  const [exportingPdf, setExportingPdf] = useState(false);
  const [viewingHistory, setViewingHistory] = useState(null);
  const [locationModal, setLocationModal] = useState(null); // pool_location object
  const [reviewsSummary, setReviewsSummary] = useState(null);
  const [qrConfig, setQrConfig] = useState(null);
  const [qrToday, setQrToday] = useState(null);
  const [dayRotation, setDayRotation] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [qrCalendar, setQrCalendar] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [timeclockRecords, setTimeclockRecords] = useState([]);
  const [timeclockDateFrom, setTimeclockDateFrom] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
  });
  const [timeclockDateTo, setTimeclockDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [timeclockSettings, setTimeclockSettings] = useState(null);

  // Workshops tab — date browser
  const [workshopDate, setWorkshopDate] = useState(() => canaryToday());
  const [dayWorkshops, setDayWorkshops]  = useState([]);
  const [wsTabFilter, setWsTabFilter]    = useState('all');

  const [monitors, setMonitors] = useState([]);
  const [monitorInvite, setMonitorInvite] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  // RGPD delete family modal
  const [deletingFamily, setDeletingFamily] = useState(null); // family object or null
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Duplicate modal
  const [duplicatingWorkshop, setDuplicatingWorkshop] = useState(null);
  const [dupMode, setDupMode] = useState('single');
  const [dupSingleDate, setDupSingleDate] = useState('');
  const [dupRangeFrom, setDupRangeFrom] = useState('');
  const [dupRangeTo, setDupRangeTo] = useState('');
  const [dupMultiDates, setDupMultiDates] = useState(['']);
  const [dupSubmitting, setDupSubmitting] = useState(false);


  const reviewsWithComments = useMemo(
    () => reviewsSummary?.reviews?.filter(r => r.comment) || [],
    [reviewsSummary]
  );

  const fetchData = useCallback(async () => {
    try {
      const [wRes, fRes, rRes, sRes] = await Promise.all([
        api.get('/workshops/all'),
        api.get('/families'),
        api.get('/reservations'),
        api.get('/stats'),
      ]);
      setWorkshops(wRes.data);
      setFamilies(fRes.data);
      setReservations(rRes.data);
      setStats(sRes.data);
      // Fetch reviews summary
      try {
        const rvRes = await api.get('/reviews/summary');
        setReviewsSummary(rvRes.data);
      } catch (err) { console.error('Reviews fetch error:', err); }
      // Fetch QR config
      try {
        const [qcRes, qtRes, qcodesRes] = await Promise.all([
          api.get('/admin/qr-config'),
          api.get('/admin/qr-today'),
          api.get('/admin/qr-codes'),
        ]);
        setQrConfig(qcRes.data);
        setQrToday(qtRes.data);
        setQrCodes(qcodesRes.data.codes || []);
      } catch (err) { console.error('QR fetch error:', err); }
      // Fetch day rotation
      try {
        const today = canaryToday();
        const drRes = await api.get(`/day-rotation/${today}`);
        setDayRotation(drRes.data);
      } catch (err) { console.error('Day rotation error:', err); }
    } catch (err) { console.error('Admin fetch error:', err); toast.error('Error al cargar datos'); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh cada 30 s + al volver a la pestaña (mismo motivo que en el
  // panel de monitores: pestañas abiertas todo el día con datos congelados).
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    const onVisible = () => { if (!document.hidden) fetchData(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchData]);

  // Fetch QR calendar when month changes
  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        const res = await api.get(`/admin/qr-calendar/${calendarMonth.year}/${calendarMonth.month}`);
        setQrCalendar(res.data);
      } catch (err) { console.error('Calendar fetch error:', err); }
    };
    fetchCalendar();
  }, [api, calendarMonth]);

  const handleSaveWorkshop = async () => {
    try {
      if (editingWorkshop) {
        await api.put(`/workshops/${editingWorkshop}`, form);
        toast.success('Taller actualizado');
      } else {
        await api.post('/workshops', form);
        toast.success('Taller creado');
      }
      setShowForm(false);
      setEditingWorkshop(null);
      setForm({ title: '', description: '', date: '', time_start: '', time_end: '', max_capacity: 15, age_min: 3, age_max: 14, category: 'general', registration_type: 'mandatory', materials: '', pool_zone: '', pool_location: null, drive_url: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleEditWorkshop = (w) => {
    setForm({ title: w.title, description: w.description, date: w.date, time_start: w.time_start, time_end: w.time_end, max_capacity: w.max_capacity, age_min: w.age_min, age_max: w.age_max, category: w.category, time_slot: w.time_slot || 'morning', registration_type: w.registration_type || 'mandatory', materials: w.materials || '', pool_zone: w.pool_zone || '', pool_location: w.pool_location || null, drive_url: w.drive_url || '' });
    setEditingWorkshop(w.id);
    setShowForm(true);
  };

  const handleDeleteWorkshop = async (id) => {
    try {
      await api.delete(`/workshops/${id}`);
      toast.success('Taller eliminado');
      fetchData();
    } catch (err) { toast.error('Error al eliminar'); }
  };

  const handleDeleteFamily = async () => {
    if (!deletingFamily) return;
    setDeleteSubmitting(true);
    try {
      const res = await api.delete(`/admin/families/${deletingFamily.user_id}`);
      const d = res.data.deleted;
      const adj = d.workshop_adjustments?.length
        ? ` Aforo ajustado en ${d.workshop_adjustments.length} taller(es).`
        : '';
      toast.success(`Familia «${d.family_name}» eliminada. ${d.reservations} reservas borradas.${adj}`);
      setDeletingFamily(null);
      setDeleteConfirmText('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar la familia');
    }
    setDeleteSubmitting(false);
  };

  const handleSaveQrConfig = async (field, value) => {
    try {
      await api.put('/admin/qr-config', { [field]: value });
      toast.success('Configuracion QR actualizada');
      fetchData();
    } catch (err) { toast.error('Error al guardar QR config'); }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const handleWorkshopDrop = async (workshopId, newData) => {
    try {
      await api.put(`/workshops/${workshopId}`, newData);
      toast.success(`Taller movido a ${newData.date} ${newData.time_start}-${newData.time_end}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al mover taller');
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get('/reports/attendance', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clubdemanri_informe_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Informe PDF descargado');
    } catch (err) {
      toast.error('Error al generar PDF');
    }
    setExportingPdf(false);
  };

  const fetchDayWorkshops = useCallback(async (date) => {
    try {
      const res = await api.get(`/workshops/by-date/${date}`);
      setDayWorkshops(res.data.sort((a, b) => a.time_start.localeCompare(b.time_start)));
    } catch { setDayWorkshops([]); }
  }, [api]);

  useEffect(() => {
    if (tab === 'workshops') fetchDayWorkshops(workshopDate);
  }, [tab, workshopDate, fetchDayWorkshops]);

  const shiftDay = (delta) => {
    const d = new Date(workshopDate + 'T12:00:00');
    d.setDate(d.getDate() + delta);
    setWorkshopDate(d.toISOString().split('T')[0]);
  };

  const fetchMonitors = useCallback(async () => {
    try {
      const [mRes, invRes] = await Promise.all([
        api.get('/admin/monitors'),
        api.get('/admin/monitor-invite'),
      ]);
      setMonitors(mRes.data);
      setMonitorInvite(invRes.data);
    } catch (err) { console.error('Monitor fetch error:', err); }
  }, [api]);

  useEffect(() => {
    if (tab === 'monitores') fetchMonitors();
  }, [tab, fetchMonitors]);

  const handleToggleMonitor = async (id, currentActive) => {
    try {
      await api.patch(`/admin/monitors/${id}`, { active: !currentActive });
      toast.success(currentActive ? 'Monitor desactivado' : 'Monitor activado');
      fetchMonitors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteMonitor = async (id, name) => {
    if (!window.confirm(`¿Eliminar al monitor "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/monitors/${id}`);
      toast.success('Monitor eliminado');
      fetchMonitors();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRegenerateInvite = async () => {
    if (!window.confirm('¿Regenerar el QR? El enlace anterior dejará de funcionar y los monitores que ya lo tengan guardado no podrán registrarse.')) return;
    setRegenerating(true);
    try {
      const res = await api.post('/admin/monitor-invite/regenerate');
      setMonitorInvite(res.data);
      toast.success('QR de alta regenerado');
    } catch { toast.error('Error al regenerar'); }
    setRegenerating(false);
  };

  const workshopStyle = (title) => {
    if (title && title.toLowerCase().includes('minigolf'))
      return { bg: '#2D6A5E12', border: '#2D6A5E40', dot: '#2D6A5E' };
    return { bg: '#A8752B12', border: '#A8752B40', dot: '#A8752B' };
  };

  const dupDates = useMemo(() => {
    if (dupMode === 'single') return dupSingleDate ? [dupSingleDate] : [];
    if (dupMode === 'range') {
      if (!dupRangeFrom || !dupRangeTo || dupRangeTo < dupRangeFrom) return [];
      const dates = [];
      const cur = new Date(dupRangeFrom + 'T12:00:00');
      const end = new Date(dupRangeTo + 'T12:00:00');
      while (cur <= end && dates.length < 366) {
        dates.push(cur.toISOString().split('T')[0]);
        cur.setDate(cur.getDate() + 1);
      }
      return dates;
    }
    if (dupMode === 'multi') return [...new Set(dupMultiDates.filter(d => d))];
    return [];
  }, [dupMode, dupSingleDate, dupRangeFrom, dupRangeTo, dupMultiDates]);

  const handleDuplicate = async () => {
    if (dupDates.length === 0) { toast.error('Selecciona al menos una fecha'); return; }
    setDupSubmitting(true);
    try {
      const res = await api.post('/workshops/bulk-duplicate', {
        source_id: duplicatingWorkshop.id,
        dates: dupDates,
      });
      const { created, skipped } = res.data;
      const msg = `${created} taller${created !== 1 ? 'es' : ''} creado${created !== 1 ? 's' : ''}` +
        (skipped > 0 ? ` · ${skipped} omitido${skipped !== 1 ? 's' : ''} (ya existían)` : '');
      toast.success(msg);
      setDuplicatingWorkshop(null);
      fetchDayWorkshops(workshopDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al duplicar');
    }
    setDupSubmitting(false);
  };

  const TABS = [
    { id: 'workshops',  label: 'Talleres',     icon: Calendar },
    { id: 'calendar',   label: 'Calendario',   icon: CalendarDays },
    { id: 'families',   label: 'Familias',     icon: Users },
    { id: 'reservations', label: 'Reservas',   icon: BarChart3 },
    { id: 'reviews',    label: 'Valoraciones', icon: Star },
    { id: 'qr',         label: 'Codigo QR',    icon: QrCode },
    { id: 'monitores',  label: 'Monitores',    icon: UserCheck },
    { id: 'cuadrante',  label: 'Cuadrante',    icon: TableProperties },
    { id: 'fichaje',    label: 'Fichaje',      icon: Fingerprint },
  ];

  return (
    <div className="min-h-screen bg-[#FBF8F3]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-black/5 px-4 sm:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading font-bold text-xl tracking-tight text-manrique-black flex items-center gap-2">
              <img src="/logo.png" alt="Parque Maritimo" className="h-9 w-auto" />
              <span>Admin</span>
            </h1>
            <p className="text-xs text-manrique-grey">Panel de administracion</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="flex items-center gap-2 text-sm font-medium text-[#48293A] border border-[#48293A]/20 hover:bg-[#48293A]/5 px-4 py-2 rounded-full transition-all disabled:opacity-50"
              data-testid="export-pdf-button"
            >
              <FileDown size={16} />
              {exportingPdf ? 'Generando...' : 'Exportar PDF'}
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-manrique-grey hover:text-manrique-black" data-testid="admin-logout-button">
              <LogOut size={16} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Familias', value: stats.complete_families, sub: `${stats.total_families} total` },
              { label: 'Talleres', value: stats.total_workshops, sub: `${dayWorkshops.length} hoy` },
              { label: 'Reservas', value: stats.total_reservations },
              { label: 'Check-ins', value: stats.checked_in },
            ].map((s, i) => (
              <div key={i} className="card-manrique p-5 text-center">
                <p className="font-heading font-bold text-3xl text-manrique-black">{s.value}</p>
                <p className="text-sm text-manrique-grey mt-1">{s.label}</p>
                {s.sub && <p className="text-xs text-manrique-muted">{s.sub}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="admin-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  tab === t.id ? 'bg-[#48293A] text-white shadow-lg shadow-[#48293A]/20' : 'bg-white text-manrique-grey border border-black/10 hover:bg-[#FBF8F3]'
                }`}
                data-testid={`tab-${t.id}`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Workshops Tab */}
        {tab === 'workshops' && (() => {
          const filtered = wsTabFilter === 'all'
            ? dayWorkshops
            : dayWorkshops.filter(w => wsTabFilter === 'minigolf'
                ? w.title.toLowerCase().includes('minigolf')
                : !w.title.toLowerCase().includes('minigolf'));
          const morning   = filtered.filter(w => w.time_slot === 'morning');
          const afternoon = filtered.filter(w => w.time_slot === 'afternoon');

          const WsCard = ({ w }) => {
            const st = workshopStyle(w.title);
            const pct = w.max_capacity > 0 ? Math.round(w.current_count / w.max_capacity * 100) : 0;
            const barColor = pct >= 90 ? '#E53E3E' : pct >= 70 ? '#DD6B20' : '#27AE60';
            const unitLabel = w.capacity_unit === 'palos' ? 'palos' : 'plazas';
            return (
              <div
                key={w.id}
                className="rounded-2xl p-4 space-y-3 border"
                style={{ backgroundColor: st.bg, borderColor: st.border }}
                data-testid={`admin-workshop-${w.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: st.dot }} />
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-sm text-manrique-black leading-tight truncate">{w.title}</p>
                      <p className="text-xs text-manrique-grey font-mono mt-0.5">{w.time_start}–{w.time_end}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => handleEditWorkshop(w)} className="p-1.5 rounded-lg hover:bg-white/60" data-testid={`edit-workshop-${w.id}`}><Pencil size={13} className="text-manrique-grey" /></button>
                    <button
                      onClick={() => { setDuplicatingWorkshop(w); setDupMode('single'); setDupSingleDate(''); setDupRangeFrom(''); setDupRangeTo(''); setDupMultiDates(['']); }}
                      className="p-1.5 rounded-lg hover:bg-white/60"
                      title="Duplicar a otras fechas"
                      data-testid={`duplicate-workshop-${w.id}`}
                    >
                      <Copy size={13} className="text-manrique-grey" />
                    </button>
                    <button onClick={() => handleDeleteWorkshop(w.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-workshop-${w.id}`}><Trash2 size={13} className="text-[#48293A]" /></button>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-manrique-grey">
                    <span>{w.current_count} / {w.max_capacity} {unitLabel}</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-black/10 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                  </div>
                </div>
              </div>
            );
          };

          const Section = ({ label, items }) => items.length === 0 ? null : (
            <div>
              <p className="text-xs font-bold text-manrique-muted uppercase tracking-widest mb-2">{label}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map(w => <WsCard key={w.id} w={w} />)}
              </div>
            </div>
          );

          return (
            <div data-testid="workshops-tab-content" className="space-y-5">
              {/* Header */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading font-bold text-xl text-manrique-black">Gestión de Talleres</h2>
                <button
                  onClick={() => { setShowForm(true); setEditingWorkshop(null); setForm({ title: '', description: '', date: workshopDate, time_start: '', time_end: '', max_capacity: 15, age_min: 3, age_max: 14, category: 'general', time_slot: 'morning', registration_type: 'mandatory', materials: '', pool_zone: '', pool_location: null, drive_url: '' }); }}
                  className="btn-manrique text-sm flex items-center gap-1"
                  data-testid="create-workshop-button"
                >
                  <Plus size={16} /> Nuevo Taller
                </button>
              </div>

              {/* Date navigator */}
              <div className="flex items-center gap-2">
                <button onClick={() => shiftDay(-1)} className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3]" data-testid="ws-prev-day">
                  <ChevronLeft size={16} />
                </button>
                <input
                  type="date"
                  value={workshopDate}
                  onChange={e => setWorkshopDate(e.target.value)}
                  className="input-manrique text-sm font-mono text-center flex-1 max-w-[180px]"
                  data-testid="ws-date-picker"
                />
                <button onClick={() => shiftDay(1)} className="w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3]" data-testid="ws-next-day">
                  <ChevronRight size={16} />
                </button>
                <span className="text-sm text-manrique-muted ml-1">{dayWorkshops.length} taller{dayWorkshops.length !== 1 ? 'es' : ''}</span>
              </div>

              {/* Activity filter pills */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'all',       label: 'Todas' },
                  { id: 'clubmanri', label: 'El Club de Manri' },
                  { id: 'minigolf',  label: 'Minigolf' },
                ].map(f => (
                  <button
                    key={f.id}
                    onClick={() => setWsTabFilter(f.id)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                      wsTabFilter === f.id
                        ? 'bg-[#48293A] text-white border-[#48293A]'
                        : 'bg-white text-manrique-grey border-black/10 hover:bg-[#FBF8F3]'
                    }`}
                    data-testid={`ws-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Grouped cards */}
              {filtered.length === 0 ? (
                <div className="card-manrique text-center py-12 text-manrique-muted">
                  <Calendar size={28} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay talleres para esta fecha</p>
                </div>
              ) : (
                <div className="space-y-6">
                  <Section label="Mañana" items={morning} />
                  <Section label="Tarde"  items={afternoon} />
                </div>
              )}
            </div>
          );
        })()}

        {/* Calendar Tab */}
        {tab === 'calendar' && (
          <div data-testid="calendar-tab-content">
            <h2 className="font-heading font-bold text-xl text-manrique-black mb-5">Calendario de Actividades</h2>
            <DailyCalendar api={api} />
          </div>
        )}

        {/* Families Tab */}
        {tab === 'families' && (
          <div data-testid="families-tab-content">
            <h2 className="font-heading font-bold text-xl text-manrique-black mb-4">Familias Registradas</h2>
            <div className="card-manrique p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/5 bg-[#FBF8F3]">
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Familia</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Email</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Telefono</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Hijos</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Ubicacion</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Estado</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {families.map((f, i) => (
                      <tr key={i} className="border-b border-black/5 last:border-0" data-testid={`family-row-${i}`}>
                        <td className="px-5 py-3 font-medium text-manrique-black">{f.family_name}</td>
                        <td className="px-5 py-3 text-manrique-grey">{f.email || f.room_number || '-'}</td>
                        <td className="px-5 py-3 text-manrique-grey">{f.phone || '-'} {f.phone_verified && <span className="text-[#27AE60]">ok</span>}</td>
                        <td className="px-5 py-3 text-manrique-grey">{f.children?.map(c => c.name).join(', ') || '-'}</td>
                        <td className="px-5 py-3 text-manrique-grey">
                          {f.pool_location ? (
                            <button
                              onClick={() => setLocationModal(f.pool_location)}
                              className="flex items-center gap-1 text-xs text-[#48293A] hover:underline font-medium"
                              data-testid={`admin-location-btn-${i}`}
                            >
                              <MapPin size={12} /> Ver ubicación
                            </button>
                          ) : '-'}
                        </td>
                        <td className="px-5 py-3">
                          {f.registration_complete ? (
                            <span className="text-xs bg-[#27AE60]/10 text-[#27AE60] px-2 py-1 rounded-full">Completo</span>
                          ) : (
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Pendiente</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setViewingHistory(f.user_id)}
                              className="flex items-center gap-1 text-xs text-[#48293A] hover:underline font-medium"
                              data-testid={`view-history-${i}`}
                            >
                              <History size={13} /> Historial
                            </button>
                            <button
                              onClick={() => { setDeletingFamily(f); setDeleteConfirmText(''); }}
                              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 hover:underline font-medium"
                              title="Eliminar familia (RGPD)"
                              data-testid={`delete-family-${i}`}
                            >
                              <Trash2 size={13} /> Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Reservations Tab */}
        {tab === 'reservations' && (
          <div data-testid="reservations-tab-content">
            <h2 className="font-heading font-bold text-xl text-manrique-black mb-1">Reservas de hoy</h2>
            <p className="text-sm text-manrique-muted mb-4">{canaryToday()} · zona horaria de Canarias</p>
            <div className="card-manrique p-0 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/5 bg-[#FBF8F3]">
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Familia</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Taller</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Ninos</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Estado</th>
                      <th className="text-left px-5 py-3 font-medium text-manrique-grey">Check-in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-5 py-10 text-center text-sm text-manrique-muted">
                          No hay reservas para hoy todavía.
                        </td>
                      </tr>
                    )}
                    {reservations.map((r, i) => (
                      <tr key={i} className="border-b border-black/5 last:border-0" data-testid={`admin-reservation-${i}`}>
                        <td className="px-5 py-3 font-medium text-manrique-black">{r.family_name}</td>
                        <td className="px-5 py-3 text-manrique-grey">{r.workshop_title}</td>
                        <td className="px-5 py-3 text-manrique-grey">{r.children_names?.join(', ')}</td>
                        <td className="px-5 py-3">
                          <span className="text-xs bg-[#48293A]/10 text-[#48293A] px-2 py-1 rounded-full">{r.status}</span>
                        </td>
                        <td className="px-5 py-3">
                          {r.checked_in ? (
                            <span className="text-xs bg-[#27AE60]/10 text-[#27AE60] px-2 py-1 rounded-full">Si</span>
                          ) : (
                            <span className="text-xs text-manrique-muted">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {tab === 'reviews' && (
          <div data-testid="reviews-tab-content">
            <h2 className="font-heading font-bold text-xl text-manrique-black mb-4">Valoraciones de Familias</h2>
            {reviewsSummary && (
              <div className="space-y-6">
                {/* Global summary */}
                <div className="card-manrique p-6 flex items-center gap-6">
                  <div className="text-center">
                    <p className="font-heading font-bold text-4xl text-manrique-black">{reviewsSummary.global_average || '-'}</p>
                    <StarDisplay rating={reviewsSummary.global_average || 0} size={18} />
                    <p className="text-xs text-manrique-muted mt-1">{reviewsSummary.total_reviews} valoraciones</p>
                  </div>
                  <div className="flex-1 h-px bg-black/5" />
                  <p className="text-sm text-manrique-grey max-w-xs">Puntuacion media global de todos los talleres valorados por las familias</p>
                </div>

                {/* Per-workshop ratings */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {reviewsSummary.workshops?.map(ws => (
                    <div key={ws.workshop_id} className="card-manrique p-5 space-y-2" data-testid={`review-workshop-${ws.workshop_id}`}>
                      <h3 className="font-heading font-bold text-base text-manrique-black">{ws.workshop_title}</h3>
                      <StarDisplay rating={ws.average_rating} count={ws.total_reviews} size={16} />
                    </div>
                  ))}
                </div>

                {/* All reviews list */}
                {reviewsWithComments.length > 0 && (
                  <div>
                    <h3 className="font-heading font-bold text-base text-manrique-black mb-3">Comentarios Recientes</h3>
                    <div className="space-y-3">
                      {reviewsWithComments.map(r => (
                        <div key={r.id} className="card-manrique p-4 flex gap-4" data-testid={`review-comment-${r.id}`}>
                          <div className="w-9 h-9 rounded-full bg-[#A8752B]/10 flex items-center justify-center flex-shrink-0">
                            <MessageSquare size={16} className="text-[#A8752B]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-medium text-manrique-black">{r.family_name}</p>
                              <StarDisplay rating={r.rating} size={12} />
                            </div>
                            <p className="text-sm text-manrique-grey mt-1">{r.workshop_title}</p>
                            <p className="text-sm text-manrique-black mt-1 italic">"{r.comment}"</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reviewsSummary.total_reviews === 0 && (
                  <div className="card-manrique text-center py-12 text-manrique-muted">
                    <Star size={32} className="mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Aun no hay valoraciones</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* QR Code Tab - QR fijo de temporada */}
        {tab === 'qr' && (
          <div data-testid="qr-tab-content" className="space-y-6">
            <h2 className="font-heading font-bold text-xl text-manrique-black">
              {qrToday?.season_fixed ? 'QR fijo de temporada' : 'Sistema de 5 QR Rotativos'}
            </h2>

            {/* Active QR Banner */}
            {qrToday && (
              <div className="card-manrique p-5 flex items-center gap-5 border-l-4 border-[#48293A]" data-testid="qr-today-banner">
                <div className="w-14 h-14 rounded-2xl bg-[#48293A] flex items-center justify-center flex-shrink-0">
                  <span className="font-heading font-bold text-2xl text-white">{qrToday.qr_number}</span>
                </div>
                {qrToday.season_fixed ? (
                  <div className="flex-1">
                    <p className="font-heading font-bold text-lg text-manrique-black">
                      QR-{qrToday.qr_number} fijo — activo toda la temporada
                    </p>
                    <p className="text-sm text-manrique-grey">
                      Rotación desactivada · Coloca solo el QR-{qrToday.qr_number} en la entrada
                      {qrToday.fixed_until ? ` (válido hasta el ${qrToday.fixed_until})` : ''}. Los demás QR ya no son válidos.
                    </p>
                  </div>
                ) : (
                  <div className="flex-1">
                    <p className="font-heading font-bold text-lg text-manrique-black">
                      Hoy toca el QR-{qrToday.qr_number}
                    </p>
                    <p className="text-sm text-manrique-grey">
                      {qrToday.date} - Coloca el QR numero {qrToday.qr_number} en la entrada
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* 5 QR Codes Grid */}
            <div>
              <h3 className="font-heading font-bold text-base text-manrique-black mb-3">Los 5 QR (imprimir y plastificar)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {qrCodes.map(qr => (
                  <div
                    key={qr.number}
                    className={`card-manrique p-4 text-center space-y-3 transition-all ${
                      qr.is_active_today ? 'ring-2 ring-[#48293A] shadow-lg' : 'opacity-60'
                    }`}
                    data-testid={`qr-card-${qr.number}`}
                  >
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full font-heading font-bold text-lg ${
                      qr.is_active_today ? 'bg-[#48293A] text-white' : 'bg-[#FBF8F3] text-manrique-grey'
                    }`}>
                      {qr.number}
                    </div>
                    <div className="flex justify-center">
                      <div className="bg-white p-2 rounded-xl border border-black/5">
                        <QRCode value={qr.url} size={120} data-testid={`qr-image-${qr.number}`} />
                      </div>
                    </div>
                    <p className="font-heading font-bold text-sm text-manrique-black">{qr.label}</p>
                    {qr.is_active_today && (
                      <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full bg-[#27AE60]/10 text-[#27AE60]">
                        {qrToday?.season_fixed ? 'FIJO TEMPORADA' : 'ACTIVO HOY'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-manrique-muted mt-3">
                {qrToday?.season_fixed
                  ? `Coloca únicamente el QR-${qrToday.qr_number} en la entrada durante toda la temporada. Los demás carteles ya no son válidos.`
                  : 'Imprime estos 5 QR una vez, plastificalos y numeralos. Cada dia coloca en la entrada el que corresponda.'}
              </p>
            </div>

            {/* Monthly Calendar */}
            <div className="card-manrique p-6 space-y-4" data-testid="qr-calendar-section">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-bold text-base text-manrique-black">Calendario Mensual de QR</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const prev = calendarMonth.month === 1
                        ? { year: calendarMonth.year - 1, month: 12 }
                        : { year: calendarMonth.year, month: calendarMonth.month - 1 };
                      setCalendarMonth(prev);
                    }}
                    className="w-8 h-8 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3]"
                    data-testid="cal-prev-month"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="font-heading font-bold text-sm text-manrique-black min-w-[140px] text-center">
                    {new Date(calendarMonth.year, calendarMonth.month - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    onClick={() => {
                      const next = calendarMonth.month === 12
                        ? { year: calendarMonth.year + 1, month: 1 }
                        : { year: calendarMonth.year, month: calendarMonth.month + 1 };
                      setCalendarMonth(next);
                    }}
                    className="w-8 h-8 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#FBF8F3]"
                    data-testid="cal-next-month"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1.5">
                {['L','M','X','J','V','S','D'].map(d => (
                  <div key={d} className="text-center text-[10px] font-bold text-manrique-muted uppercase py-1">{d}</div>
                ))}
                {qrCalendar && (() => {
                  const firstDay = new Date(calendarMonth.year, calendarMonth.month - 1, 1).getDay();
                  const offset = firstDay === 0 ? 6 : firstDay - 1;
                  const today = canaryToday();
                  const QR_COLORS = ['#48293A', '#48293A', '#27AE60', '#A8752B', '#844E33'];
                  return (
                    <>
                      {Array.from({ length: offset }, (_, i) => (
                        <div key={`empty-${i}`} />
                      ))}
                      {qrCalendar.days.map(d => {
                        const isToday = d.date === today;
                        const color = QR_COLORS[d.qr_number - 1];
                        return (
                          <div
                            key={d.date}
                            className={`text-center rounded-xl py-2 px-1 cursor-default transition-all ${
                              isToday ? 'ring-2 ring-manrique-black shadow-md' : ''
                            }`}
                            style={{ backgroundColor: `${color}15` }}
                            data-testid={`cal-day-${d.day}`}
                          >
                            <p className="text-xs font-medium text-manrique-grey">{d.day}</p>
                            <p className="font-heading font-bold text-sm" style={{ color }}>
                              {d.qr_number}
                            </p>
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-3 pt-2 border-t border-black/5">
                {[1,2,3,4,5].map(n => {
                  const QR_COLORS = ['#48293A', '#48293A', '#27AE60', '#A8752B', '#844E33'];
                  return (
                    <div key={n} className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: QR_COLORS[n-1] }} />
                      <span className="text-xs text-manrique-grey font-medium">QR-{n}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-manrique p-6 space-y-5" data-testid="qr-config-card">
                <div className="flex items-center gap-2">
                  <Settings size={18} className="text-manrique-grey" />
                  <h3 className="font-heading font-bold text-base text-manrique-black">Configuracion</h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Hora de activacion</label>
                  <input
                    type="time"
                    value={qrConfig?.active_from || '08:00'}
                    onChange={e => handleSaveQrConfig('active_from', e.target.value)}
                    className="input-manrique"
                    data-testid="qr-active-from"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Hora de desactivacion</label>
                  <input
                    type="time"
                    value={qrConfig?.active_until || '20:00'}
                    onChange={e => handleSaveQrConfig('active_until', e.target.value)}
                    className="input-manrique"
                    data-testid="qr-active-until"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Talleres por familia/dia</label>
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        onClick={() => handleSaveQrConfig('daily_workshop_limit', n)}
                        className={`w-12 h-12 rounded-2xl font-heading font-bold text-lg transition-all ${
                          (qrConfig?.daily_workshop_limit || 2) === n
                            ? 'bg-[#48293A] text-white shadow-lg'
                            : 'bg-[#FBF8F3] text-manrique-grey hover:bg-[#48293A]/10'
                        }`}
                        data-testid={`qr-limit-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card-manrique p-6 space-y-4">
                <div className="bg-[#27AE60]/5 border border-[#27AE60]/20 rounded-2xl p-4 space-y-2">
                  <p className="text-sm font-medium text-[#27AE60]">Como funciona</p>
                  <ul className="text-xs text-manrique-grey space-y-1.5">
                    <li>- 5 QR permanentes plastificados y numerados</li>
                    <li>- Cada dia se coloca uno diferente en la entrada</li>
                    <li>- Rotacion automatica: dia 1,6,11... = QR-1, dia 2,7,12... = QR-2, etc.</li>
                    <li>- Si alguien le hace foto, al dia siguiente ya no funciona</li>
                    <li>- Solo activo en horario configurado</li>
                  </ul>
                </div>

                {/* Day Rotation Config */}
                <div className="space-y-3 pt-2">
                  <h4 className="font-heading font-bold text-sm text-manrique-black">Rotacion de Dias (Talleres)</h4>
                  {dayRotation && (
                    <div className="bg-[#FBF8F3] rounded-2xl p-4 space-y-2">
                      <p className="text-xs text-manrique-muted">Hoy ({dayRotation.date}): Tipo <span className="font-bold text-manrique-black">{dayRotation.day_type}</span></p>
                      <p className="text-xs">AM: <span className="font-medium">{dayRotation.description_morning}</span></p>
                      <p className="text-xs">PM: <span className="font-medium">{dayRotation.description_afternoon}</span></p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={async () => { const today = canaryToday(); await api.put(`/admin/day-rotation/${today}`, { type: 'A' }); toast.success('Dia cambiado a Tipo A'); fetchData(); }}
                      className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${dayRotation?.day_type === 'A' ? 'bg-[#48293A] text-white' : 'bg-[#FBF8F3] text-manrique-grey hover:bg-[#48293A]/10'}`}
                      data-testid="set-day-a"
                    >
                      Tipo A
                    </button>
                    <button
                      onClick={async () => { const today = canaryToday(); await api.put(`/admin/day-rotation/${today}`, { type: 'B' }); toast.success('Dia cambiado a Tipo B'); fetchData(); }}
                      className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${dayRotation?.day_type === 'B' ? 'bg-[#48293A] text-white' : 'bg-[#FBF8F3] text-manrique-grey hover:bg-[#48293A]/10'}`}
                      data-testid="set-day-b"
                    >
                      Tipo B
                    </button>
                  </div>
                  <button
                    onClick={async () => { const today = canaryToday(); await api.delete(`/admin/day-rotation/${today}`); toast.success('Rotacion automatica restaurada'); fetchData(); }}
                    className="w-full text-xs text-manrique-muted hover:underline"
                    data-testid="reset-rotation"
                  >
                    Restaurar rotacion automatica
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Monitores Tab */}
        {tab === 'monitores' && (
          <div className="space-y-8" data-testid="monitores-tab-content">
            <h2 className="font-heading font-bold text-xl text-manrique-black">Gestión de Monitores</h2>

            {/* Tabla de monitores */}
            <div>
              <h3 className="font-heading font-bold text-base text-manrique-black mb-3">Monitores registrados</h3>
              <div className="card-manrique p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/5 bg-[#FBF8F3]">
                        <th className="text-left px-5 py-3 font-medium text-manrique-grey">Nombre</th>
                        <th className="text-left px-5 py-3 font-medium text-manrique-grey">Email</th>
                        <th className="text-left px-5 py-3 font-medium text-manrique-grey">Estado</th>
                        <th className="text-left px-5 py-3 font-medium text-manrique-grey">Alta</th>
                        <th className="text-left px-5 py-3 font-medium text-manrique-grey"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {monitors.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-5 py-10 text-center text-manrique-muted text-sm">
                            No hay monitores registrados todavía.
                          </td>
                        </tr>
                      ) : monitors.map(m => (
                        <tr key={m.id} className="border-b border-black/5 last:border-0" data-testid={`monitor-row-${m.id}`}>
                          <td className="px-5 py-3 font-medium text-manrique-black">{m.name}</td>
                          <td className="px-5 py-3 text-manrique-grey">{m.email}</td>
                          <td className="px-5 py-3">
                            {m.active ? (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#27AE60]/10 text-[#27AE60] px-2.5 py-1 rounded-full font-semibold">
                                <ShieldCheck size={11} /> Activo
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs bg-[#A8752B]/15 text-[#A8752B] px-2.5 py-1 rounded-full font-semibold animate-pulse">
                                ● Pendiente
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3 text-xs text-manrique-muted">
                            {m.created_at ? new Date(m.created_at).toLocaleDateString('es-ES') : '-'}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleToggleMonitor(m.id, m.active)}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                                  m.active
                                    ? 'bg-[#4C4D57]/10 text-[#4C4D57] hover:bg-[#4C4D57]/20'
                                    : 'bg-[#27AE60]/10 text-[#27AE60] hover:bg-[#27AE60]/20'
                                }`}
                                data-testid={`toggle-monitor-${m.id}`}
                              >
                                {m.active ? 'Desactivar' : 'Activar'}
                              </button>
                              <button
                                onClick={() => handleDeleteMonitor(m.id, m.name)}
                                className="p-1.5 rounded-lg hover:bg-red-50 text-[#48293A] hover:text-red-600 transition-colors"
                                data-testid={`delete-monitor-${m.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* QR de alta */}
            <div className="card-manrique p-6 space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <QrCode size={18} className="text-[#48293A]" />
                  <h3 className="font-heading font-bold text-base text-manrique-black">QR de alta de monitores</h3>
                </div>
                <button
                  onClick={handleRegenerateInvite}
                  disabled={regenerating}
                  className="flex items-center gap-2 text-sm font-medium text-[#48293A] border border-[#48293A]/20 hover:bg-[#48293A]/5 px-4 py-2 rounded-full transition-all disabled:opacity-50"
                  data-testid="regenerate-invite-btn"
                >
                  <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
                  Regenerar QR
                </button>
              </div>

              {monitorInvite && (
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="bg-white p-3 rounded-2xl border border-black/8 shadow-sm flex-shrink-0">
                    <QRCode
                      value={`https://elclubdemanri.es${monitorInvite.qr_url}`}
                      size={160}
                      data-testid="monitor-invite-qr"
                    />
                  </div>
                  <div className="space-y-3 text-sm">
                    <p className="text-manrique-black font-medium">El monitor escanea este QR con su móvil, rellena sus datos y queda pendiente de activación.</p>
                    <p className="text-manrique-grey">URL del enlace:</p>
                    <code className="block text-xs bg-[#FBF8F3] rounded-xl px-3 py-2 text-[#48293A] break-all border border-[#EDE5D8]">
                      https://elclubdemanri.es{monitorInvite.qr_url}
                    </code>
                    <div className="bg-[#A8752B]/8 border border-[#A8752B]/20 rounded-xl p-3">
                      <p className="text-xs text-[#A8752B] font-medium">⚠ Regenerar el QR invalida el enlace anterior. Los monitores que ya lo tengan no podrán usarlo.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Fichaje Tab */}
        {tab === 'fichaje' && (
          <div className="space-y-6" data-testid="fichaje-tab-content">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="font-heading font-bold text-xl text-manrique-black">Gestion de Fichaje Laboral</h2>
              <a
                href={`${api.defaults.baseURL}/admin/timeclock/export?from=${timeclockDateFrom}&to=${timeclockDateTo}`}
                target="_blank" rel="noopener noreferrer"
                className="btn-manrique text-sm flex items-center gap-2"
                onClick={async (e) => {
                  e.preventDefault();
                  try {
                    const res = await api.get(`/admin/timeclock/export?from=${timeclockDateFrom}&to=${timeclockDateTo}`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement('a'); a.href = url; a.download = `fichaje_${timeclockDateFrom}_${timeclockDateTo}.pdf`; a.click();
                  } catch { toast.error('Error al exportar'); }
                }}
                data-testid="export-timeclock-pdf"
              >
                <FileDown size={16} /> Exportar PDF
              </a>
            </div>

            {/* Date filters */}
            <div className="card-manrique p-5">
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <label className="block text-xs font-medium text-manrique-black mb-1">Desde</label>
                  <input type="date" value={timeclockDateFrom} onChange={e => setTimeclockDateFrom(e.target.value)} className="input-manrique" data-testid="timeclock-date-from" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-manrique-black mb-1">Hasta</label>
                  <input type="date" value={timeclockDateTo} onChange={e => setTimeclockDateTo(e.target.value)} className="input-manrique" data-testid="timeclock-date-to" />
                </div>
                <button
                  onClick={async () => {
                    try {
                      const [rRes, sRes] = await Promise.all([
                        api.get(`/admin/timeclock/records?from=${timeclockDateFrom}&to=${timeclockDateTo}`),
                        api.get('/timeclock/settings'),
                      ]);
                      setTimeclockRecords(rRes.data);
                      setTimeclockSettings(sRes.data);
                    } catch { toast.error('Error al cargar fichajes'); }
                  }}
                  className="btn-manrique text-sm"
                  data-testid="timeclock-search"
                >
                  Buscar
                </button>
                <div className="ml-auto flex items-center gap-2">
                  <label className="text-xs text-manrique-grey">Pausas:</label>
                  <button
                    onClick={async () => {
                      try {
                        const res = await api.put('/admin/timeclock/settings', { breaks_enabled: !timeclockSettings?.breaks_enabled });
                        setTimeclockSettings(res.data);
                        toast.success(res.data.breaks_enabled ? 'Pausas activadas' : 'Pausas desactivadas');
                      } catch {}
                    }}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${timeclockSettings?.breaks_enabled ? 'bg-[#27AE60] text-white' : 'bg-[#FBF8F3] text-manrique-grey'}`}
                    data-testid="toggle-breaks"
                  >
                    {timeclockSettings?.breaks_enabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </div>

            {/* Records table */}
            <div className="card-manrique overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#FBF8F3] text-manrique-grey text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 text-left">Fecha</th>
                      <th className="px-4 py-3 text-left">Monitor</th>
                      <th className="px-4 py-3 text-center">Entrada</th>
                      <th className="px-4 py-3 text-center">Ini. Pausa</th>
                      <th className="px-4 py-3 text-center">Fin Pausa</th>
                      <th className="px-4 py-3 text-center">Salida</th>
                      <th className="px-4 py-3 text-center">Trabajo</th>
                      <th className="px-4 py-3 text-center">Pausa</th>
                      <th className="px-4 py-3 text-center">GPS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeclockRecords.length === 0 ? (
                      <tr><td colSpan={9} className="px-4 py-8 text-center text-manrique-muted">Sin registros en el periodo seleccionado. Pulsa "Buscar".</td></tr>
                    ) : (
                      timeclockRecords.map((r, i) => {
                        const punches = {};
                        (r.punches || []).forEach(p => { punches[p.type] = p; });
                        const hasGps = (r.punches || []).some(p => p.lat);
                        return (
                          <tr key={i} className={`border-t border-black/5 ${i % 2 === 0 ? '' : 'bg-[#FBF8F3]/50'}`}>
                            <td className="px-4 py-3 font-medium text-manrique-black">{r.date}</td>
                            <td className="px-4 py-3 text-manrique-grey">{r.user_name}</td>
                            <td className="px-4 py-3 text-center font-mono text-[#27AE60]">{punches.clock_in?.time?.slice(0,5) || '-'}</td>
                            <td className="px-4 py-3 text-center font-mono text-[#A8752B]">{punches.break_start?.time?.slice(0,5) || '-'}</td>
                            <td className="px-4 py-3 text-center font-mono text-[#A8752B]">{punches.break_end?.time?.slice(0,5) || '-'}</td>
                            <td className="px-4 py-3 text-center font-mono text-[#48293A]">{punches.clock_out?.time?.slice(0,5) || '-'}</td>
                            <td className="px-4 py-3 text-center font-bold text-manrique-black">{r.total_work_str}</td>
                            <td className="px-4 py-3 text-center text-manrique-muted">{r.total_break_str}</td>
                            <td className="px-4 py-3 text-center">{hasGps ? <span className="text-[#27AE60] text-xs">Si</span> : <span className="text-manrique-muted text-xs">-</span>}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-manrique-muted">
              Conforme al Real Decreto-ley 8/2019. Art. 34.9 Estatuto de los Trabajadores. Conservacion obligatoria: 4 anos.
            </p>
          </div>
        )}

        {/* ── TAB: Cuadrante de monitores ─────────────────────────────────────── */}
        {tab === 'cuadrante' && (
          <div className="space-y-5" data-testid="cuadrante-tab-content">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-heading font-bold text-xl text-manrique-black flex items-center gap-2">
                  <TableProperties size={20} /> Cuadrante semanal de monitores
                </h2>
                <p className="text-sm text-manrique-muted mt-1">
                  Haz clic en cualquier celda para editar. Los cambios se guardan al instante.
                </p>
              </div>
            </div>
            <ScheduleTable editable={true} />
          </div>
        )}

      </main>

      {/* Workshop Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="workshop-form-modal">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-bold text-xl text-manrique-black">
                {editingWorkshop ? 'Editar Taller' : 'Nuevo Taller'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-[#FBF8F3] rounded-full" data-testid="close-workshop-form">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Titulo</label>
                <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="input-manrique" data-testid="workshop-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Descripcion</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input-manrique h-20 py-3" data-testid="workshop-description-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Fecha</label>
                  <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="input-manrique" data-testid="workshop-date-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Inicio</label>
                  <input type="time" value={form.time_start} onChange={e => setForm({...form, time_start: e.target.value})} className="input-manrique" data-testid="workshop-start-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Fin</label>
                  <input type="time" value={form.time_end} onChange={e => setForm({...form, time_end: e.target.value})} className="input-manrique" data-testid="workshop-end-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Capacidad</label>
                  <input type="number" value={form.max_capacity} onChange={e => setForm({...form, max_capacity: parseInt(e.target.value) || 0})} className="input-manrique" data-testid="workshop-capacity-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Edad min</label>
                  <input type="number" value={form.age_min} onChange={e => setForm({...form, age_min: parseInt(e.target.value) || 0})} className="input-manrique" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Edad max</label>
                  <input type="number" value={form.age_max} onChange={e => setForm({...form, age_max: parseInt(e.target.value) || 0})} className="input-manrique" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Categoria</label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="input-manrique" data-testid="workshop-category-select">
                  <option value="general">General</option>
                  <option value="arte">Arte</option>
                  <option value="manualidades">Manualidades</option>
                  <option value="agua">Agua</option>
                  <option value="cocina">Cocina</option>
                  <option value="deporte">Deporte</option>
                  <option value="naturaleza">Naturaleza</option>
                  <option value="musica">Musica</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Tipo de acceso</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({...form, registration_type: 'mandatory'})} className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${form.registration_type === 'mandatory' ? 'bg-[#48293A] text-white' : 'bg-[#FBF8F3] text-manrique-grey'}`} data-testid="type-mandatory">
                    Registro obligatorio
                  </button>
                  <button type="button" onClick={() => setForm({...form, registration_type: 'free'})} className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${form.registration_type === 'free' ? 'bg-[#27AE60] text-white' : 'bg-[#FBF8F3] text-manrique-grey'}`} data-testid="type-free">
                    Acceso libre
                  </button>
                </div>
                <p className="text-xs text-manrique-muted mt-1">
                  {form.registration_type === 'mandatory' ? 'Las familias deben reservar plaza para esta actividad' : 'Las familias pueden acceder sin reserva previa'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Horario</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({...form, time_slot: 'morning'})} className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${form.time_slot === 'morning' ? 'bg-[#A8752B] text-white' : 'bg-[#FBF8F3] text-manrique-grey'}`} data-testid="slot-morning">
                    AM - Manana
                  </button>
                  <button type="button" onClick={() => setForm({...form, time_slot: 'afternoon'})} className={`flex-1 py-3 rounded-2xl text-sm font-medium transition-all ${form.time_slot === 'afternoon' ? 'bg-[#48293A] text-white' : 'bg-[#FBF8F3] text-manrique-grey'}`} data-testid="slot-afternoon">
                    PM - Tarde
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Materiales necesarios</label>
                <textarea value={form.materials} onChange={e => setForm({...form, materials: e.target.value})} placeholder="Ej: Pinturas, pinceles, papel..." className="input-manrique h-16 py-3" data-testid="workshop-materials-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Zona de la piscina</label>
                <PoolMap value={form.pool_location} onChange={(loc) => setForm({...form, pool_location: loc, pool_zone: loc.zone === 'libre' ? 'Posicion libre' : `Zona ${loc.zone}`})} />
              </div>
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Enlace Google Drive (recursos)</label>
                <input value={form.drive_url} onChange={e => setForm({...form, drive_url: e.target.value})} placeholder="https://drive.google.com/drive/folders/..." className="input-manrique" data-testid="workshop-drive-url-input" />
              </div>
              <button onClick={handleSaveWorkshop} className="btn-manrique w-full mt-2" data-testid="save-workshop-button">
                {editingWorkshop ? 'Actualizar Taller' : 'Crear Taller'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Workshop Modal */}
      {duplicatingWorkshop && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="duplicate-modal">
          <div className="bg-white rounded-3xl p-8 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-xl text-manrique-black">Duplicar taller</h3>
                <p className="text-sm text-manrique-grey mt-0.5 truncate max-w-[300px]">
                  {duplicatingWorkshop.title} · {duplicatingWorkshop.time_start}–{duplicatingWorkshop.time_end}
                </p>
              </div>
              <button onClick={() => setDuplicatingWorkshop(null)} className="p-1 hover:bg-[#FBF8F3] rounded-full flex-shrink-0 ml-3" data-testid="close-duplicate-modal">
                <X size={20} />
              </button>
            </div>

            {/* Mode selector */}
            <div className="flex gap-1 bg-[#FBF8F3] p-1.5 rounded-2xl mb-5">
              {[
                { id: 'single', label: 'Un día' },
                { id: 'range',  label: 'Rango' },
                { id: 'multi',  label: 'Varios días' },
              ].map(m => (
                <button
                  key={m.id}
                  onClick={() => setDupMode(m.id)}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    dupMode === m.id ? 'bg-white text-manrique-black shadow-sm' : 'text-manrique-grey hover:text-manrique-black'
                  }`}
                  data-testid={`dup-mode-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            {/* Single date */}
            {dupMode === 'single' && (
              <div>
                <label className="block text-sm font-medium text-manrique-black mb-1">Fecha destino</label>
                <input
                  type="date"
                  value={dupSingleDate}
                  onChange={e => setDupSingleDate(e.target.value)}
                  className="input-manrique"
                  data-testid="dup-single-date"
                />
              </div>
            )}

            {/* Range */}
            {dupMode === 'range' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Desde</label>
                  <input
                    type="date"
                    value={dupRangeFrom}
                    onChange={e => setDupRangeFrom(e.target.value)}
                    className="input-manrique"
                    data-testid="dup-range-from"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-manrique-black mb-1">Hasta</label>
                  <input
                    type="date"
                    value={dupRangeTo}
                    onChange={e => setDupRangeTo(e.target.value)}
                    className="input-manrique"
                    data-testid="dup-range-to"
                  />
                </div>
              </div>
            )}

            {/* Multi */}
            {dupMode === 'multi' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-manrique-black mb-1">Fechas</label>
                {dupMultiDates.map((d, i) => (
                  <div key={i} className="flex gap-2">
                    <input
                      type="date"
                      value={d}
                      onChange={e => { const next = [...dupMultiDates]; next[i] = e.target.value; setDupMultiDates(next); }}
                      className="input-manrique flex-1"
                      data-testid={`dup-multi-date-${i}`}
                    />
                    {dupMultiDates.length > 1 && (
                      <button
                        onClick={() => setDupMultiDates(dupMultiDates.filter((_, idx) => idx !== i))}
                        className="p-2 text-manrique-muted hover:text-[#48293A] rounded-xl hover:bg-[#FBF8F3]"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={() => setDupMultiDates([...dupMultiDates, ''])}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border-2 border-dashed border-[#48293A]/30 text-[#48293A] text-sm font-medium hover:bg-[#48293A]/5 transition-colors"
                  data-testid="dup-add-date"
                >
                  <Plus size={16} /> Añadir fecha
                </button>
              </div>
            )}

            {/* Preview */}
            {dupDates.length > 0 && (
              <div className="mt-4 bg-[#FBF8F3] rounded-2xl p-4">
                <p className="text-xs font-bold text-manrique-muted uppercase tracking-widest mb-2">
                  {dupDates.length} fecha{dupDates.length !== 1 ? 's' : ''} seleccionada{dupDates.length !== 1 ? 's' : ''}
                </p>
                {dupDates.length > 10 ? (
                  <p className="text-sm text-manrique-grey">
                    Del <span className="font-medium text-manrique-black">{dupDates[0]}</span> al <span className="font-medium text-manrique-black">{dupDates[dupDates.length - 1]}</span>
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {dupDates.map(d => (
                      <span key={d} className="text-xs bg-[#48293A]/10 text-[#48293A] px-2.5 py-1 rounded-full font-medium">{d}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={handleDuplicate}
              disabled={dupDates.length === 0 || dupSubmitting}
              className="btn-manrique w-full mt-5 disabled:opacity-40 flex items-center justify-center gap-2"
              data-testid="dup-confirm-btn"
            >
              {dupSubmitting
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <>Duplicar en {dupDates.length} fecha{dupDates.length !== 1 ? 's' : ''}</>
              }
            </button>
          </div>
        </div>
      )}

      {/* RGPD — Delete family modal */}
      {deletingFamily && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delete-family-modal">
          <div className="bg-[#FBF8F3] rounded-3xl max-w-md w-full shadow-2xl p-8 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-heading font-bold text-xl text-manrique-black">Eliminar familia (RGPD)</h3>
                <p className="text-sm text-manrique-grey mt-1">Derecho de supresión — esta acción es <strong>permanente e irreversible</strong>.</p>
              </div>
              <button onClick={() => setDeletingFamily(null)} className="p-1.5 hover:bg-white rounded-full shrink-0" data-testid="close-delete-family-modal">
                <X size={20} />
              </button>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800 space-y-1">
              <p>Se borrará <strong>todo lo asociado</strong> a la familia <strong>«{deletingFamily.family_name}»</strong>:</p>
              <ul className="list-disc list-inside mt-2 space-y-0.5 text-red-700">
                <li>Cuenta de usuario y perfil familiar</li>
                <li>Todas sus reservas (con ajuste de aforo en talleres)</li>
                <li>Sesiones QR, códigos OTP, PDFs de inscripción</li>
                <li>Historial de resets de contraseña</li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-manrique-black">
                Escribe <span className="font-bold text-red-700">«{deletingFamily.family_name}»</span> para confirmar:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={deletingFamily.family_name}
                className="w-full border border-black/15 rounded-xl px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-400"
                data-testid="delete-family-confirm-input"
                autoComplete="off"
              />
            </div>

            <button
              onClick={handleDeleteFamily}
              disabled={deleteConfirmText.trim().toLowerCase() !== deletingFamily.family_name.trim().toLowerCase() || deleteSubmitting}
              className="w-full py-2.5 rounded-xl font-semibold text-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
              data-testid="confirm-delete-family-btn"
            >
              {deleteSubmitting
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><Trash2 size={15} /> Eliminar definitivamente</>
              }
            </button>
          </div>
        </div>
      )}

      {/* Family History Modal */}
      {viewingHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="family-history-modal">
          <div className="bg-[#FBF8F3] rounded-3xl max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-3xl px-8 py-5 border-b border-black/5 flex items-center justify-between z-10">
              <h3 className="font-heading font-bold text-xl text-manrique-black">Historial de Familia</h3>
              <button onClick={() => setViewingHistory(null)} className="p-1.5 hover:bg-[#FBF8F3] rounded-full" data-testid="close-history-modal">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <FamilyHistory api={api} isAdmin={true} userId={viewingHistory} />
            </div>
          </div>
        </div>
      )}

      {/* Modal de ubicación de familia */}
      {locationModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setLocationModal(null)}
          data-testid="admin-location-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 space-y-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-manrique-black flex items-center gap-2">
                <MapPin size={18} className="text-[#48293A]" /> Ubicación en la piscina
              </h3>
              <button onClick={() => setLocationModal(null)} className="p-1.5 hover:bg-[#FBF8F3] rounded-full transition-colors">
                <X size={20} />
              </button>
            </div>
            <PoolMap value={locationModal} onChange={() => {}} readOnly />
          </div>
        </div>
      )}
    </div>
  );
}
